/*
  MotorLens cart helpers.
  <ml-free-shipping>: shows how far the cart is from free shipping. The threshold is set in GBP
  and converted with Shopify's own rate, the same conversion checkout applies to shipping rules.
  Being a custom element, it updates itself whenever Concept re-renders the cart drawer.
*/
(() => {
  if (customElements.get('ml-free-shipping')) return;

  const currency = () => (window.Shopify && Shopify.currency) || { active: 'GBP', rate: '1' };

  const format = (cents) => {
    const { active } = currency();
    const amount = cents / 100;
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: active,
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2
    }).format(amount);
  };

  class MlFreeShipping extends HTMLElement {
    connectedCallback() {
      const { rate } = currency();
      const threshold = Math.ceil(parseFloat(this.dataset.thresholdGbp) * parseFloat(rate || '1')) * 100;
      const total = parseInt(this.dataset.total, 10) || 0;
      const remaining = threshold - total;
      const text = this.querySelector('[data-ml-ship-text]');
      const bar = this.querySelector('[data-ml-ship-bar]');

      if (remaining <= 0) {
        text.textContent = 'You’ve unlocked free tracked shipping';
        this.classList.add('ml-ship--done');
      } else {
        text.textContent = `You’re ${format(remaining)} away from free tracked shipping`;
      }
      bar.style.width = `${Math.min(100, Math.round((total / threshold) * 100))}%`;

      // Flag the add-on when adding it would unlock free shipping
      const scope = this.closest('cart-drawer, .drawer, main') || document;
      const upsell = scope.querySelector('[data-ml-upsell-price]');
      const hint = upsell && upsell.querySelector('[data-ml-upsell-hint]');
      if (hint) hint.hidden = !(remaining > 0 && parseInt(upsell.dataset.mlUpsellPrice, 10) >= remaining);
    }
  }

  customElements.define('ml-free-shipping', MlFreeShipping);

  // Any GBP amount marked data-ml-gbp (announcement bar, trust strip, delivery block) in the visitor's currency
  const convertGbp = () => {
    const { active, rate } = currency();
    if (active === 'GBP' || !rate) return;
    document.querySelectorAll('[data-ml-gbp]').forEach((el) => {
      el.textContent = format(Math.ceil(parseFloat(el.dataset.mlGbp) * parseFloat(rate)) * 100);
    });
  };
  convertGbp();
  document.addEventListener('shopify:section:load', convertGbp);
})();

/*
  Opt-in shipping protection (snippets/ml-protection.liquid).
  Uses /cart/update.js with the cart sections bundled in, then hands the result to Concept's
  cartUpdate pub/sub so the cart redraws in place exactly as it does for quantity changes.
  Listeners are delegated because the theme replaces the cart markup on every update.
*/
(() => {
  if (window.mlProtection) return;
  window.mlProtection = true;

  const box = () => document.querySelector('[data-ml-protect]');
  let busy = false;

  const update = (variantId, quantity) => {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
    const root = (window.theme && theme.routes && theme.routes.root_url) || '/';
    return fetch(`${root.replace(/\/?$/, '/')}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: { [variantId]: quantity }, sections })
    })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((cart) => {
        if (window.theme && theme.pubsub && cart.sections && Object.keys(cart.sections).length) {
          // The redraw resets the scroll position; keep the toggle where the customer's thumb is
          const before = box() && box().getBoundingClientRect().top;
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'ml-protection', cart });
          const keep = () => { const el = box(); if (el && before != null) window.scrollBy(0, el.getBoundingClientRect().top - before); };
          requestAnimationFrame(keep);
          setTimeout(keep, 120);
        } else {
          window.location.reload();
        }
        return cart;
      });
  };

  // Keep protection at one, and never leave it alone in the cart
  const tidy = (cart) => {
    const el = box();
    if (!el || busy) return;
    const id = Number(el.dataset.variant);
    const qty = cart ? cart.items.filter((i) => i.variant_id === id).reduce((s, i) => s + i.quantity, 0) : Number(el.dataset.qty);
    const others = cart ? cart.item_count - qty : Number(el.dataset.others);
    if (qty > 0 && others === 0) { busy = true; update(id, 0).finally(() => { busy = false; }); }
    else if (qty > 1) { busy = true; update(id, 1).finally(() => { busy = false; }); }
  };

  document.addEventListener('change', (event) => {
    const input = event.target.closest && event.target.closest('[data-ml-protect] input');
    if (!input) return;
    event.stopPropagation();
    const el = box();
    const note = el.querySelector('[data-ml-protect-note]');
    input.disabled = true;
    note.textContent = 'Updating…';
    busy = true;
    update(el.dataset.variant, input.checked ? 1 : 0)
      .catch(() => {
        input.checked = !input.checked;
        input.disabled = false;
        note.textContent = 'Sorry, that didn’t work. Please try again.';
      })
      .finally(() => { busy = false; });
  }, true);

  const subscribe = () => {
    if (window.theme && theme.pubsub) {
      theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (e) => {
        if (e && e.source !== 'ml-protection' && e.cart && Array.isArray(e.cart.items)) setTimeout(() => tidy(e.cart), 50);
      });
    }
    tidy();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', subscribe); else subscribe();
})();
